function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  // Helper function to get matching inputs and calculate totals
  getYardageData() {
    if (this.input.dataset.productType !== "Custom Curtain" && this.input.dataset.productType !== "Roman Shade") {
      return null;
    }

    const max = parseInt(this.input.max);
    const yardsPerUnit = parseInt(this.input.dataset.yards) || 1;
    const currentVariantId = this.input.dataset.inventoryProductId;
    
    // Get matching inputs in the same cart section
    const currentContainer = this.input.closest('#main-cart-items, #CartDrawer-CartItems');
    const matchingInputs = currentContainer
      ? currentContainer.querySelectorAll(`input[data-inventory-product-id="${currentVariantId}"]`)
      : [this.input];
    
    // Calculate total quantity (all inputs)
    let totalQuantity = 0;
    matchingInputs.forEach(input => {
      const yards = parseInt(input.dataset.yards) || 0;
      const quantity = parseInt(input.value) || 0;
      totalQuantity += yards * quantity;
    });
    
    // Calculate total quantity excluding current input
    let totalQuantityExcludingCurrent = 0;
    matchingInputs.forEach(input => {
      if (input !== this.input) {
        const yards = parseInt(input.dataset.yards) || 0;
        const quantity = parseInt(input.value) || 0;
        totalQuantityExcludingCurrent += yards * quantity;
      }
    });
    
    return {
      max,
      yardsPerUnit,
      matchingInputs,
      totalQuantity,
      totalQuantityExcludingCurrent,
      remainingYards: max - totalQuantityExcludingCurrent,
      remainingQuantity: (max - totalQuantity) / yardsPerUnit
    };
  }

  onInputChange(event) {
    const yardageData = this.getYardageData();
    
    if (yardageData) {
      const value = parseInt(this.input.value) || 0;
      const maxAllowedQuantity = Math.floor(yardageData.remainingYards / yardageData.yardsPerUnit);
      
      // If user entered more than allowed, set to max allowed
      if (value > maxAllowedQuantity) {
        this.input.value = Math.max(0, maxAllowedQuantity);
      }
    }

    
    this.validateQtyRules();
    
    // Show error messages for manual input changes
    if (yardageData) {
      this.showInventoryError(yardageData);
    }
  }


  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

    validateQtyRules() {
        const value = parseInt(this.input.value);

        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']");
            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }

        if (this.input.max) {
            const buttonPlus = this.querySelector(".quantity__button[name='plus']");
            const yardageData = this.getYardageData();

            if (yardageData) {
                // Set data-total-purchased attribute on all matching inputs
                yardageData.matchingInputs.forEach(input => {
                    input.setAttribute('data-total-purchased', yardageData.totalQuantity);
                });

                // Disable/enable input and plus button based on remaining quantity
                if (yardageData.remainingQuantity < 1) {
                    this.input.setAttribute('disabled', 'disabled');
                    this.input.classList.add('disabled');
                    buttonPlus.classList.add('disabled');
                    buttonPlus.setAttribute('disabled', 'disabled');
                } else {
                    buttonPlus.classList.remove('disabled');
                    this.input.classList.remove('disabled');
                    this.input.removeAttribute('disabled');
                    buttonPlus.removeAttribute('disabled');
                }

                // Show/hide error message for inventory exceeded
                this.showInventoryError(yardageData);
            } else {
                // Standard product (not Custom Curtain or Roman Shade)
                const max = parseInt(this.input.max);
                buttonPlus.classList.toggle('disabled', value >= max);
            }
        }
    }

    showInventoryError(yardageData) {
        // Check if current input exceeds remaining yards
        const currentYards = parseInt(this.input.dataset.yards) || 1;
        const currentQuantity = parseInt(this.input.value) || 0;
        const currentTotalYards = currentYards * currentQuantity;
        
        if (currentTotalYards <= yardageData.remainingYards) {
            // Hide any existing error
            this.hideInventoryError();
            return;
        }
        const buttonMinus = this.querySelector(".quantity__button[name='minus']");
        buttonMinus.classList.add('disabled');
        buttonMinus.setAttribute('disabled', 'disabled');
        
        const errorMessage = `Insufficient inventory! Remove the item and add again with the available inventory`;

        // Try to find existing error element first
        let errorElement = this.querySelector('.cart-item__error') || 
                          this.querySelector('#CartDrawer-LineItemError-' + this.input.dataset.index) ||
                          this.querySelector('#Line-item-error-' + this.input.dataset.index);

        if (errorElement) {
            // Use existing error element
            const errorText = errorElement.querySelector('.cart-item__error-text');
            if (errorText) {
                errorText.textContent = errorMessage;
                errorElement.style.display = 'block';
                errorElement.classList.add('cart-item__error--visible');
            }
        } else {
            // Create new error element
            this.createInventoryError(errorMessage);
        }
    }

    createInventoryError(message) {
        // Find existing custom error container
        const cartContext = this.closest('#main-cart-items') ? 'main' : 'drawer';
        const uniqueId = `custom-inventory-error-${cartContext}-${this.input.dataset.index}`;
        const errorContainer = document.getElementById(uniqueId);
        
        if (!errorContainer) {
            console.warn('Custom inventory error container not found:', uniqueId);
            return;
        }

        // Update error text
        const errorText = errorContainer.querySelector('.cart-item__error-text');
        if (errorText) {
            errorText.textContent = message;
        }

        // Show the error container
        errorContainer.style.display = 'flex';
    }

    hideInventoryError() {
        // Hide existing error elements
        const existingError = this.querySelector('.cart-item__error');
        if (existingError) {
            existingError.style.display = 'none';
            existingError.classList.remove('cart-item__error--visible');
        }

        // Hide custom error using unique ID
        const cartContext = this.closest('#main-cart-items') ? 'main' : 'drawer';
        const uniqueId = `custom-inventory-error-${cartContext}-${this.input.dataset.index}`;
        const customError = document.getElementById(uniqueId);
        if (customError) {
            customError.style.display = 'none';
        }
    }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // Close all sibling details elements immediately (accordion behavior) - only if opening a new one
      if (!isOpen) {
        const parentList = detailsElement.closest('ul');
        if (parentList) {
          const siblingDetails = parentList.querySelectorAll('details[open]');
          siblingDetails.forEach((siblingDetail) => {
            if (siblingDetail !== detailsElement && siblingDetail.hasAttribute('open')) {
              this.closeSubmenuImmediate(siblingDetail);
            }
          });
        }
      }
      
      // Use requestAnimationFrame to ensure siblings are closed before opening animation starts
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (detailsElement.hasAttribute('open')) {
            detailsElement.classList.add('menu-opening');
            summaryElement.setAttribute('aria-expanded', true);
            parentMenuElement && parentMenuElement.classList.add('submenu-open');
            !reducedMotion || reducedMotion.matches
              ? addTrapFocus()
              : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
          }
        }, reducedMotion && reducedMotion.matches ? 0 : 50);
      });
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeSubmenuImmediate(detailsElement) {
    // Close immediately without animation for accordion behavior
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    const summary = detailsElement.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', false);
      removeTrapFocus(summary);
    }
    detailsElement.removeAttribute('open');
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }
  connectedCallback() {
    if (this.moved) return;
    this.moved = true;

    // Move to body (once), but defer section lookup
    document.body.appendChild(this);

    // Defer until Turbo has updated the DOM
    requestAnimationFrame(() => this.setSectionFromClosest());
  }

  setSectionFromClosest() {
    const section = this.closest('.shopify-section');
    if (section && section.id) {
      this.dataset.section = section.id.replace('shopify-section-', '');
    } else {
      console.warn('ModalDialog: .shopify-section not found or missing ID');
    }
  }

  // connectedCallback() {
  //   if (this.moved) return;
  //   this.moved = true;
  //   this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
  //   document.body.appendChild(this);
  // }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      if (this.reducedMotion.matches) this.pause(); else this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    const focusableSelector = 'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';
    this.sliderItemsToShow.forEach((item, index) => {
      const focusables = item.querySelectorAll(focusableSelector);
      if (index === this.currentPage - 1) {
        focusables.forEach((el) => el.removeAttribute('tabindex'));
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        focusables.forEach((el) => el.setAttribute('tabindex', '-1'));
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide?.classList.add(`${animationClassOut}-${direction}`);
    nextSlide?.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide?.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide?.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if(input){
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// Inventory validation for checkout
class InventoryValidator {
  static validationDisabled = false;
  //production
  static STOREFRONT_ACCESS_TOKEN = 'b43981be1914378f3981388c8ce15fd5';
  //staging
  //static STOREFRONT_ACCESS_TOKEN = '8275852ecf3a0d7ead6410e7306b4b90';
  
  
  static getShopDomain() {
    // Get domain from current page URL
    return window.location.hostname;
  }
  
  static async validateCheckout() {
    try {
      // Get current cart data
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      const inventoryGroups = {};
      const errors = [];

      // Group cart items by inventory product (for Custom Curtain / Roman Shade)
      cart.items.forEach(item => {
        const inventoryProductId = item.properties?._inventory_product_id;
        const yardsPerUnit = Math.ceil(item.properties?._yards_per_unit || '1');
        const productType = item.product_type;
        
        if (inventoryProductId && 
            (productType === 'Custom Curtain' || productType === 'Roman Shade')) {  
          if (!inventoryGroups[inventoryProductId]) {
            inventoryGroups[inventoryProductId] = {
              totalYards: 0,
              handle: item.properties._inventory_product_handle,
              productTitle: item.product_title,
              items: []
            };
          }
          
          const itemYards = yardsPerUnit * item.quantity;
          inventoryGroups[inventoryProductId].totalYards += itemYards;
          inventoryGroups[inventoryProductId].items.push({
            title: item.product_title,
            quantity: item.quantity,
            yardsPerUnit: yardsPerUnit,
            totalYards: itemYards
          });
        }
      });

      // Check each inventory product using Storefront API
      for (const [inventoryId, data] of Object.entries(inventoryGroups)) {
        try {
          const availableInventory = await this.getProductInventory(data.handle);
          
          // If inventory is not tracked (null), do not block checkout
          if (availableInventory == null) {
            continue;
          }
          
          if (data.totalYards > availableInventory) {
            const shortage = data.totalYards - availableInventory;
            errors.push({
              productTitle: data.productTitle,
              required: data.totalYards,
              available: availableInventory,
              shortage: shortage,
              items: data.items
            });
          }
        } catch (error) {
          
          errors.push({
            productTitle: data.productTitle,
            error: 'Unable to verify inventory. Please try again.',
            items: data.items
          });
        }
      }

      // Also validate regular product variant inventory (including BOGO with Sold As)
      // Uses Storefront API for real-time inventory (DOM data could be stale)
      const variantValidation = await this.validateVariantInventory(cart);
      if (!variantValidation.isValid) {
        errors.push(...variantValidation.errors);
      }

      return {
        isValid: errors.length === 0,
        errors: errors
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [{
          error: 'Unable to validate inventory. Please refresh and try again.'
        }]
      };
    }
  }

  /**
   * Check if variant inventory is tracked (same logic as sold-as-inventory.js).
   * Inventory is tracked only when: inventory_management === 'shopify' && inventory_policy !== 'continue'.
   * Reads from cart DOM (data-inventory-tracked) which is set with that condition in Liquid.
   * @param {string} variantId - Variant ID
   * @returns {boolean} - true if tracked (validate), false if not tracked (skip validation)
   */
  static isVariantInventoryTracked(variantId) {
    const input = document.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
    if (!input) return true; // not in DOM (e.g. drawer not opened), fall back to API
    const tracked = input.getAttribute('data-inventory-tracked');
    return tracked !== 'false';
  }

  /**
   * Validate variant inventory for regular products
   * Groups items by variant ID and calculates actual units needed (considering _units_per)
   * Uses Storefront API for real-time inventory data
   */
  static async validateVariantInventory(cart) {
    const errors = [];
    
    try {
      // Group cart items by variant ID, calculating total units needed
      const variantGroups = {};
      
      cart.items.forEach(item => {
        // Skip Custom Curtain and Roman Shade (handled separately)
        if (item.product_type === 'Custom Curtain' || item.product_type === 'Roman Shade') {
          return;
        }
        
        // Skip swatches
        if (item.product_type === 'Swatches') {
          return;
        }
        
        const variantId = String(item.variant_id);
        const unitsPer = parseInt(item.properties?._units_per || '1', 10);
        
        // Calculate actual inventory units this line item consumes
        // For "Sold As Pair" (_units_per = 2), each cart qty = 2 inventory units
        const actualUnits = item.quantity * unitsPer;
        
        if (!variantGroups[variantId]) {
          variantGroups[variantId] = {
            totalUnits: 0,
            productHandle: item.handle,
            productTitle: item.product_title,
            variantTitle: item.variant_title,
            variantId: variantId,
            items: []
          };
        }
        
        variantGroups[variantId].totalUnits += actualUnits;
        variantGroups[variantId].items.push({
          title: item.product_title,
          quantity: item.quantity,
          unitsPer: unitsPer,
          actualUnits: actualUnits
        });
      });
      
      // Check inventory for each variant using Storefront API
      for (const [variantId, data] of Object.entries(variantGroups)) {
        try {
          // Same logic as sold-as-inventory.js: skip validation when inventory is not tracked
          if (!this.isVariantInventoryTracked(variantId)) {
            continue;
          }
          const availableInventory = await this.getVariantInventory(data.productHandle, variantId);
          // If API says not tracked (null/undefined), do not block checkout
          if (availableInventory == null) {
            continue;
          }
          
          if (data.totalUnits > availableInventory) {
            const variantText = data.variantTitle && data.variantTitle !== 'Default Title' 
              ? ` - ${data.variantTitle}` 
              : '';
            errors.push({
              productTitle: `${data.productTitle}${variantText}`,
              required: data.totalUnits,
              available: availableInventory,
              shortage: data.totalUnits - availableInventory,
              items: data.items
            });
          }
        } catch (error) {
          console.error(`Error checking variant inventory for ${variantId}:`, error);
          // Don't block checkout on error, just skip this item
        }
      }
      
      return {
        isValid: errors.length === 0,
        errors: errors
      };
    } catch (error) {
      console.error('Error validating variant inventory:', error);
      return { isValid: true, errors: [] }; // Don't block on error
    }
  }

  /**
   * Get variant inventory using Storefront API
   */
  static async getVariantInventory(productHandle, variantId) {
    const query = `
      query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          variants(first: 100) {
            edges {
              node {
                id
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    try {
      const shopDomain = this.getShopDomain();
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { handle: productHandle }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL query failed: ${data.errors[0]?.message || 'Unknown error'}`);
      }

      const variants = data.data?.productByHandle?.variants?.edges;
      if (!variants) {
        throw new Error(`Product not found: ${productHandle}`);
      }

      // Find matching variant by ID (Storefront API returns GID format)
      const gidVariantId = `gid://shopify/ProductVariant/${variantId}`;
      const variant = variants.find(v => v.node.id === gidVariantId);
      
      if (!variant) {
        throw new Error(`Variant not found: ${variantId}`);
      }

      // quantityAvailable can be null if inventory tracking is disabled
      return variant.node.quantityAvailable;
      
    } catch (error) {
      console.error(`Error fetching variant inventory for ${productHandle}/${variantId}:`, error);
      throw error;
    }
  }

  static async getProductInventory(productHandle) {
    const query = `
      query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          id
          title
          totalInventory
        }
      }
    `;

    try {
      const shopDomain = this.getShopDomain();
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { handle: productHandle }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL query failed: ${data.errors[0]?.message || 'Unknown error'}`);
      }

      const product = data.data?.productByHandle;
      if (!product) {
        throw new Error(`Product not found: ${productHandle}`);
      }

      // Return null when inventory is not tracked (Storefront API returns null for totalInventory)
      if (product.totalInventory == null) {
        return null;
      }
      return product.totalInventory;
      
    } catch (error) {
      console.error(`❌ Error fetching product inventory via Storefront API for ${productHandle}:`, error);
      throw error;
    }
  }

  static showCheckoutError(errors) {
    // Find existing error container
    const errorContainer = document.getElementById('inventory-checkout-error');
    if (!errorContainer) {
      console.warn('Inventory checkout error container not found');
      return;
    }

    // Find the items container
    const itemsContainer = document.getElementById('inventory-error-items');
    if (!itemsContainer) {
      console.warn('Inventory error items container not found');
      return;
    }

    // Clear previous content
    itemsContainer.innerHTML = '';

    // Add error content
    errors.forEach(error => {
      if (error.error) {
        // Generic error message
        const errorDiv = document.createElement('p');
        errorDiv.className = 'inventory-error-message';
        errorDiv.textContent = error.error;
        itemsContainer.appendChild(errorDiv);
      } else if (error.productTitle && typeof error.available !== 'undefined') {
        // Inventory shortage error with specific details
        const errorDiv = document.createElement('div');
        errorDiv.className = 'inventory-error-item';
        errorDiv.style.cssText = 'margin: 8px 0; padding: 8px 12px; background: rgba(255,255,255,0.5); border-radius: 4px;';
        
        const titleP = document.createElement('p');
        titleP.className = 'inventory-error-title';
        titleP.style.cssText = 'margin: 0 0 4px 0; font-weight: 600;';
        titleP.textContent = error.productTitle;
        errorDiv.appendChild(titleP);
        
        const detailP = document.createElement('p');
        detailP.className = 'inventory-error-detail';
        detailP.style.cssText = 'margin: 0; font-size: 13px;';
        detailP.textContent = `Requested: ${error.required} | Available: ${error.available}`;
        errorDiv.appendChild(detailP);
        
        itemsContainer.appendChild(errorDiv);
      } else {
        // Legacy format - items list
        const errorDiv = document.createElement('div');
        errorDiv.className = 'inventory-error-item';
        
        if (error.items && error.items.length > 0) {
          const itemsDiv = document.createElement('div');
          itemsDiv.className = 'inventory-error-items-list';
          
          const titleP = document.createElement('p');
          titleP.className = 'inventory-error-title';
          titleP.textContent = 'Items in cart:';
          itemsDiv.appendChild(titleP);
          
          // Group items by title to avoid duplicates
          const groupedItems = {};
          error.items.forEach(item => {
            if (groupedItems[item.title]) {
              groupedItems[item.title].quantity += item.quantity;
              groupedItems[item.title].totalYards += item.totalYards || 0;
            } else {
              groupedItems[item.title] = {
                title: item.title,
                quantity: item.quantity,
                totalYards: item.totalYards || 0
              };
            }
          });
          
          // Display unique products with combined quantities
          Object.values(groupedItems).forEach(item => {
            const itemP = document.createElement('p');
            itemP.className = 'inventory-error-item-text';
            itemP.textContent = `• Product: ${item.title} (Quantity: ${item.quantity})`;
            itemsDiv.appendChild(itemP);
          });
          
          errorDiv.appendChild(itemsDiv);
        }
        
        itemsContainer.appendChild(errorDiv);
      }
    });

    // Show the error container
    errorContainer.style.display = 'block';

    // Scroll to error
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Hide Option Validation Methods
  static async validateHideOption() {
    try {
      // Get cart items from DOM (they have data attributes we need)
      const cartItems = document.querySelectorAll('.cart-item[data-product-handle]');
      const blockedItems = [];
      
      const processedVariantIds = new Set();
      
      // Check each cart item
      for (const item of cartItems) {
        const handle = item.dataset.productHandle;
        const variantId = item.dataset.variantId;
        const productTitle = item.dataset.productTitle;
        const variantTitle = item.dataset.variantTitle;
        
        if (!handle || !variantId) continue;
        
        // Skip if we've already checked this variant
        if (processedVariantIds.has(variantId)) continue;
        processedVariantIds.add(variantId);
        
        try {
          const hideOption = await this.getVariantHideOption(handle, variantId);
          
          if (hideOption === true || hideOption === 'true') {
            blockedItems.push({
              title: productTitle,
              variantTitle: variantTitle,
              variantId: variantId
            });
          }
        } catch (error) {
          console.error(`Error checking hide_option for ${handle}:`, error);
          // Don't block checkout on error, just skip this item
        }
      }
      
      return {
        isValid: blockedItems.length === 0,
        blockedItems: blockedItems
      };
    } catch (error) {
      console.error('Error validating hide_option:', error);
      return { isValid: true, blockedItems: [] }; // Don't block on error
    }
  }

  static async getVariantHideOption(productHandle, variantId) {
    // Fetch product JSON to get variant metafields
    const response = await fetch(`/products/${productHandle}.json`);
    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.status}`);
    }
    
    const data = await response.json();
    const product = data.product;
    
    // Find the variant
    const variant = product.variants?.find(v => String(v.id) === String(variantId));
    
    if (!variant) {
      return false;
    }
    
    // Check hide_option metafield - it should be in variant metafields
    // The metafield might be at variant level or we need to use Storefront API
    // For now, let's check if it exists in the variant object
    const hideOption = variant.metafields?.global?.hide_option;
    
    // If not found in JSON, we need to use Storefront API
    if (hideOption === undefined) {
      return await this.getVariantHideOptionFromStorefront(productHandle, variantId);
    }
    
    return hideOption;
  }

  static async getVariantHideOptionFromStorefront(productHandle, variantId) {
    const query = `
      query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          variants(first: 100) {
            edges {
              node {
                id
                hideOption: metafield(namespace: "global", key: "hide_option") {
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const shopDomain = this.getShopDomain();
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { handle: productHandle }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
    
      
      if (data.errors) {
        throw new Error(`GraphQL query failed: ${data.errors[0]?.message || 'Unknown error'}`);
      }

      const variants = data.data?.productByHandle?.variants?.edges;
      if (!variants) return false;

      // Find matching variant by ID (Storefront API returns GID format)
      const gidVariantId = `gid://shopify/ProductVariant/${variantId}`;
      const variant = variants.find(v => v.node.id === gidVariantId);
      
      if (!variant) return false;
      
      const hideOptionValue = variant.node.hideOption?.value;
      return hideOptionValue === 'true' || hideOptionValue === true;
      
    } catch (error) {
      console.error(`Error fetching hide_option via Storefront API for ${productHandle}:`, error);
      return false; // Don't block on error
    }
  }

  static showHideOptionError(blockedItems) {
    const errorContainer = document.getElementById('hide-option-checkout-error');
    if (!errorContainer) return;

    const itemsContainer = document.getElementById('hide-option-error-items');
    if (!itemsContainer) return;

    // Clear previous content
    itemsContainer.innerHTML = '';

    // Add error content for each blocked item
    blockedItems.forEach(item => {
      const itemP = document.createElement('p');
      itemP.style.cssText = 'margin: 4px 0; font-size: 12px; padding: 4px 8px; background: rgba(255,255,255,0.5); border-radius: 4px;';
      // Include variant title if available and not "Default Title"
      const variantText = item.variantTitle && item.variantTitle !== 'Default Title' ? ` - ${item.variantTitle}` : '';
      itemP.textContent = `• ${item.title}${variantText} is out of stock`;
      itemsContainer.appendChild(itemP);
    });

    // Show the error container
    errorContainer.style.display = 'block';
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  static hideHideOptionError() {
    const errorContainer = document.getElementById('hide-option-checkout-error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }

  static hideCheckoutError() {
    const existingError = document.getElementById('inventory-checkout-error');
    if (existingError) {
      existingError.style.display = 'none';
    }
  }

  static async blockCheckout(event) {
    // If validation is disabled, let the checkout proceed normally
    if (this.validationDisabled) {
      return;
    }
    
    // Skip redirect for draft order button - it has its own handler
    const button = event.target;
    if (button.id === 'create-draft-order-btn') {
      return;
    }
    
    // Show loading state
    const originalText = button.textContent;
    button.disabled = true;

    try {
      // First, validate hide_option (out of stock check)
      const hideOptionValidation = await this.validateHideOption();
      
      if (!hideOptionValidation.isValid) {
        event.preventDefault();
        event.stopPropagation();
        
        // Hide inventory error and show hide_option error
        this.hideCheckoutError();
        this.showHideOptionError(hideOptionValidation.blockedItems);
        button.textContent = originalText;
        button.disabled = false;
        return false;
      }
      
      // Hide any existing hide_option errors
      this.hideHideOptionError();
      
      // Then validate inventory
      const validation = await this.validateCheckout();
      
      if (validation.isValid) {
        // Hide any existing errors
        this.hideCheckoutError();
        button.textContent = originalText;
        button.disabled = false;  
        window.location.href = '/checkout';      
        return true;
      } else {
        
        event.preventDefault();
        event.stopPropagation();
        
        this.showCheckoutError(validation.errors);
        button.textContent = originalText;
        button.disabled = false;
        return false;
      }
    } catch (error) {
      
      event.preventDefault();
      event.stopPropagation();
      this.showCheckoutError([{ error: 'Validation failed. Please try again.' }]);
      button.textContent = originalText;
      button.disabled = false;
      return false;
    }
  }
}

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdateInMiniCart, (data) => {
    //console.log("mini cart updated");
        initializeCheckoutValidation();
    });
} else {
  console.error('subscribe or PUB_SUB_EVENTS not available');
} 
window.addEventListener('DOMContentLoaded', () => {
  initializeCheckoutValidation();
});
// Function to initialize checkout validation
function initializeCheckoutValidation() {
    //console.log("initializeCheckoutValidation");
  // Hook into checkout buttons
  const checkoutButtons = document.querySelectorAll(
    '#checkout, #CartDrawer-Checkout, .cart__checkout-button, #create-draft-order-btn'
  );
  
  checkoutButtons.forEach(button => {
    // Remove any existing listeners to avoid duplicates
    button.removeEventListener('click', InventoryValidator.blockCheckout);
    // Add new listener
    button.addEventListener('click', function(event) {
      InventoryValidator.blockCheckout(event);
    });
  });

  // Also hook into form submissions
  const cartForms = document.querySelectorAll('form[action*="cart"], form#cart, form#CartDrawer-Form');
  cartForms.forEach(form => {
    form.removeEventListener('submit', handleFormSubmit);
    form.addEventListener('submit', handleFormSubmit);
  });
}

// Form submit handler
function handleFormSubmit(event) {
  const submitButton = event.submitter;
  if (submitButton && (submitButton.name === 'checkout' || submitButton.id === 'checkout')) {
    InventoryValidator.blockCheckout(event);
  }
}
